import React, { Component } from "react";
import TextField from "@material-ui/core/TextField";
import MuiPhoneNumber from "material-ui-phone-number";
import { connect } from "react-redux";
import { saveCustomerContact } from "../../store/actions/customers";
import { getCheckoutContactIssues } from "../domain/checkoutFlow";
import StorefrontActionButton from "../../Components/Storefront/ActionButton/StorefrontActionButton";

const sameId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  `${left}` === `${right}`;

export const getCheckoutPhoneCountryOptions = ({
  settings = {},
  countries = [],
  address = {},
} = {}) => {
  const selectedCountry = countries.find((country) =>
    sameId(country?.id, address?.country),
  );
  const selectedName = `${selectedCountry?.name || ""}`.trim().toLowerCase();
  if (
    selectedName === "nigeria" ||
    (countries.length === 1 &&
      `${countries[0]?.name || ""}`.trim().toLowerCase() === "nigeria")
  )
    return ["ng"];
  if (
    settings.force_phone_code_match_country &&
    settings.enable_gcc &&
    selectedCountry?.dial_code
  )
    return [`${selectedCountry.dial_code}`.toLowerCase()];
  return settings.show_all_country_codes
    ? []
    : [
        "kw",
        "sa",
        "qa",
        "om",
        "ae",
        "bh",
        "sy",
        "lb",
        "jo",
        "eg",
        "us",
        "uk",
        "cn",
      ];
};

export class ContactEditor extends Component {
  state = { draft: { ...this.props.contact }, saving: false, error: "" };

  componentDidUpdate(prevProps) {
    if (prevProps.contact !== this.props.contact && !this.state.saving)
      this.setState({ draft: { ...this.props.contact } });
  }

  patch = (changes) =>
    this.setState((state) => ({
      draft: { ...state.draft, ...changes },
      error: "",
    }));

  save = async () => {
    const issues = getCheckoutContactIssues(
      this.state.draft,
      this.props.settings,
      false,
      { address: this.props.address, countries: this.props.countries },
    );
    if (issues.length) {
      this.setState({ error: issues[0] });
      return;
    }
    this.setState({ saving: true, error: "" });
    try {
      await this.props.saveContact({
        ...this.state.draft,
        birthday:
          this.state.draft.birthday || this.state.draft.birth_date || "",
        optInMarketing: this.state.draft.opt_in_marketing,
        contactChannel: this.state.draft.contact_channel,
      });
      this.setState({ saving: false });
      this.props.onSaved();
    } catch (error) {
      this.setState({ saving: false, error: "contact_unavailable" });
    }
  };

  render() {
    const arabic = this.props.language === "arabic";
    const { draft } = this.state;
    const selectedCountry = (this.props.countries || []).find((country) =>
      sameId(country?.id, this.props.address?.country),
    );
    const phoneCountries = getCheckoutPhoneCountryOptions(this.props);
    const phoneDisabled =
      this.props.auth?.user?.provider === "phone" ||
      ((this.props.settings.enable_aseer_time ||
      (this.props.settings.enable_teasers_staff && this.props.settings.enable_teasers)) &&
      !(this.props.profileMode && !this.props.auth?.user?.phone));
    const errorText = {
      contact_unavailable: arabic ? "تعذر حفظ بياناتك. حاول مرة أخرى." : "We couldn’t save your details. Please try again.",
      contact_name_required: arabic ? "أدخل الاسم." : "Enter your name.",
      contact_name_too_long: arabic
        ? "يجب ألا يتجاوز الاسم 25 حرفاً."
        : "Name must be 25 characters or fewer.",
      contact_phone_invalid: arabic
        ? "أدخل رقم هاتف دولياً صالحاً."
        : "Enter a valid international phone number.",
      contact_phone_country_mismatch: arabic
        ? "يجب أن يطابق مفتاح الهاتف دولة التوصيل."
        : "Phone country code must match the delivery country.",
      contact_email_invalid: arabic
        ? "أدخل بريداً إلكترونياً صالحاً."
        : "Enter a valid email address.",
      contact_civil_id_required: arabic
        ? "أدخل الرقم المدني."
        : "Enter the Civil ID.",
    }[this.state.error];
    return (
      <div style={{ display: "grid", gap: 14, direction: arabic ? "rtl" : "ltr" }}>
        <TextField
          variant="outlined"
          autoFocus={this.props.autoFocus !== false}
          required
          label={arabic ? "الاسم" : "Name"}
          value={draft.name || ""}
          onChange={(event) => this.patch({ name: event.target.value })}
        />
        <MuiPhoneNumber
          variant="outlined"
          key={selectedCountry?.dial_code || this.props.store?.default_dial_code || "kw"}
          required
          defaultCountry={
            selectedCountry?.dial_code ||
            this.props.store?.default_dial_code ||
            "kw"
          }
          onlyCountries={phoneCountries}
          excludeCountries={["il"]}
          countryCodeEditable={false}
          disableAreaCodes
          disabled={phoneDisabled}
          fullWidth
          autoComplete="tel"
          error={this.state.error === "contact_phone_invalid" || this.state.error === "contact_phone_country_mismatch"}
          label={arabic ? "رقم الهاتف" : "Phone"}
          value={draft.phone || ""}
          onChange={(phone) => this.patch({ phone })}
        />
        {(this.props.settings.show_customer_email_field ||
          this.props.settings.email_required) && (
          <TextField
          variant="outlined"
            required={this.props.settings.email_required === true}
            type="email"
            label={arabic ? "البريد الإلكتروني" : "Email"}
            value={draft.email || ""}
            disabled={
              this.props.auth?.user?.provider !== "phone" &&
              Boolean(this.props.auth?.user?.email)
            }
            onChange={(event) => this.patch({ email: event.target.value })}
          />
        )}
        {this.props.settings.ask_for_civil_id && (
          <TextField
          variant="outlined"
            required
            label={arabic ? "الرقم المدني" : "Civil ID"}
            value={draft.civilID || ""}
            onChange={(event) => this.patch({ civilID: event.target.value })}
          />
        )}
        {this.state.error && (
          <p role="alert" style={{ color: "#b00020", margin: 0 }}>
            {errorText ||
              (arabic
                ? "أكمل بيانات التواصل المطلوبة."
                : "Complete the required contact details.")}
          </p>
        )}
        <StorefrontActionButton
          fullWidth
          disabled={this.state.saving}
          onClick={this.save}
        >
          {this.props.submitLabel || (arabic ? "حفظ ومتابعة" : "Save and continue")}
        </StorefrontActionButton>
      </div>
    );
  }
}

export default connect(
  (state) => ({
    language: state.store?.language || "english",
    store: state.store?.store || {},
    settings: state.store?.store?.settings || {},
    auth: state.auth || {},
    address: state.customers?.address || {},
    countries:
      state.store?.store?.all_countries ||
      state.store?.store?.countries ||
      [],
    contact: state.customers?.customerContact || {},
  }),
  (dispatch) => ({
    saveContact: (contact) => dispatch(saveCustomerContact(contact)),
  }),
)(ContactEditor);
